import { ComponentType, lazy } from 'react'

const RELOAD_FLAG_PREFIX = 'lazy-retry-'

const lazyWithRetry = <T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>,
  name: string
) =>
  lazy(async () => {
    const key = RELOAD_FLAG_PREFIX + name
    try {
      const mod = await factory()
      sessionStorage.removeItem(key)
      return mod
    } catch {
      try {
        const mod = await factory()
        sessionStorage.removeItem(key)
        return mod
      } catch (retryErr) {
        // One retry above failed too — hard-reload once to fetch fresh modules.
        // The guard (sessionStorage is per-document, so per OpenFin view) stops a
        // reload loop on a genuinely broken module; success clears it so a later
        // transient failure can reload again.
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1')
          location.reload()
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return new Promise<T>(() => {}) // hold Suspense until the reload lands
        }
        throw retryErr
      }
    }
  })

export const ActivityLogGrid = lazyWithRetry(
  () => import('../components/ActivityLog/Grid/ActivityLogGrid'),
  'ActivityLogGrid'
)
export const AlertFilterEditor = lazyWithRetry(
  () => import('../components/AlertFilter/AlertFilterEditor'),
  'AlertFilterEditor'
)
export const BenchmarkDataContainer = lazyWithRetry(
  () => import('../components/BenchmarkData/BenchmarkContainer'),
  'BenchmarkDataContainer'
)
export const PassiveOrders = lazyWithRetry(
  () => import('../components/PassiveOrders/PassiveOrders'),
  'PassiveOrders'
)
export const RFQManager = lazyWithRetry(
  () => import('../components/RFQManager/RFQManager'),
  'RFQManager'
)
export const SecuritiesTable = lazyWithRetry(
  () => import('../components/SecuritiesTable'),
  'SecuritiesTable'
)
export const SettingsModal = lazyWithRetry(
  () => import('../components/SettingsModal/SettingsModal'),
  'SettingsModal'
)
export const TradeBlotterGrid = lazyWithRetry(
  () => import('../components/TradeBlotter/Grid/TradeBlotterGrid'),
  'TradeBlotterGrid'
)

// LIST TRADING ROUTES
export const CreateTradingList = lazyWithRetry(
  () => import('../containers/TradingList/CreateEditTradingList'),
  'CreateTradingList'
)
export const TradeWatchList = lazyWithRetry(
  () => import('../containers/TradingList/TradeWatchList'),
  'TradeWatchList'
)
export const TradingListLayout = lazyWithRetry(
  () => import('../containers/TradingList/TradingListLayout'),
  'TradingListLayout'
)

// ADMIN ROUTES
export const ActivityTicker = lazyWithRetry(
  () => import('../components/Admin/ActivityTicker/ActivityTicker'),
  'ActivityTicker'
)
export const AggressAttemptsGrid = lazyWithRetry(
  () => import('../components/Admin/AggressAttempts/Grid/AggressAttemptsGrid'),
  'AggressAttemptsGrid'
)
export const BBMISIN = lazyWithRetry(
  () => import('../components/Admin/BBMISIN/BBMISIN'),
  'BBMISIN'
)
export const ExternalLiquidityStats = lazyWithRetry(
  () =>
    import('../components/Admin/ExternalLiquidityStats/ExternalLiquidityStats'),
  'ExternalLiquidityStats'
)
export const ManagementReportMainPage = lazyWithRetry(
  () =>
    import('../components/Admin/ManagementReports/ManagementReportMainPage'),
  'ManagementReportMainPage'
)
export const QueueStats = lazyWithRetry(
  () => import('../components/Admin/QueueStats/QueueStats'),
  'QueueStats'
)
export const RestrictedTrades = lazyWithRetry(
  () => import('../components/Admin/RestrictedTrades/RestrictedTrades'),
  'RestrictedTrades'
)
export const SystemParameters = lazyWithRetry(
  () => import('../components/Admin/SystemParameters/SystemParameters'),
  'SystemParameters'
)
export const Tiers = lazyWithRetry(
  () => import('../components/Admin/Tiers/Tiers'),
  'Tiers'
)
export const UserDashboard = lazyWithRetry(
  () => import('../components/Admin/UserDashboard/UserDashboard'),
  'UserDashboard'
)
export const UserData = lazyWithRetry(
  () => import('../components/Admin/UserData/UserData'),
  'UserData'
)
export const WatchlistManager = lazyWithRetry(
  () => import('../components/Admin/WatchlistManager/WatchlistManager'),
  'WatchlistManager'
)

// OPENFIN WINDOW ENTRY POINTS
export const OpenFinAggressor = lazyWithRetry(
  () => import('../containers/OpenFin/OpenFinAggressor'),
  'OpenFinAggressor'
)
export const OpenFinDepth = lazyWithRetry(
  () => import('../containers/OpenFin/OpenFinDepth'),
  'OpenFinDepth'
)
export const OpenFinLastLook = lazyWithRetry(
  () => import('../containers/OpenFin/OpenFinLastLook'),
  'OpenFinLastLook'
)
export const OpenFinRFQ = lazyWithRetry(
  () => import('../containers/OpenFin/OpenFinRFQ'),
  'OpenFinRFQ'
)
export const OpenFinTradeConfirm = lazyWithRetry(
  () => import('../containers/OpenFin/OpenFinTradeConfirm'),
  'OpenFinTradeConfirm'
)
export const OpenFinUserEditor = lazyWithRetry(
  () => import('../containers/OpenFin/Admin/OpenFinUserEditor'),
  'OpenFinUserEditor'
)
export const OpenFinWatchlist = lazyWithRetry(
  () => import('../containers/OpenFin/OpenFinWatchlist'),
  'OpenFinWatchlist'
)
